import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  message,
  Input,
} from 'antd';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import { isMutable } from 'utils/estadosGlobales';
import { getModelPermission } from 'utils/permission/index';
import ModalDelete from 'components/ModalDelete';
import {
  deleteAsignacionDePlacas,
  getAsignacionDePlacas,
  patchAsignacionDePlacas,
  postAsignacionDePlacas,
  baseURI,
} from 'api/recaudacion/asignacionDePlacas';
import { onError } from 'utils/handlers';
import { DoubleRightOutlined, RightOutlined } from '@ant-design/icons';
import { getManyPlacas } from 'api/recaudacion';
import {
  getConfiguracionesDeAsignacionDePlacas,
  getManyConfiguracionesAsignaciones,
} from 'api/recaudacion/configuracionDeAsignacionDePlacas';
import NumericInput from 'components/NumericInput';

// eslint-disable-next-line react/prop-types
const Placas = ({ permission, usuario }) => {
  // eslint-disable-next-line no-param-reassign
  permission.import = true;
  const [form] = Form.useForm();
  const configuracion_de_asignacion_de_placa = Form.useWatch('configuracion_de_asignacion_de_placa', form);
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [mutable, setMutable] = useState(true);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [placas, setPlacas] = useState([]);
  const [configuraciones, setConfiguraciones] = useState([]);
  const [configuracionesTemp, setConfiguracionesTemp] = useState([]);
  const configuracionesRef = useRef({ results: [] });

  const sorter = (a, b) => a.orden - b.orden;

  const normalize = (_data) => {
    const _normalized = [];
    _data.forEach((item) => {
      const idx = _normalized.findIndex((arr) => arr.some((e) => e
        .configuracion_de_asignacion_de_placa === item.configuracion_de_asignacion_de_placa));
      if (idx !== -1) {
        _normalized[idx].push(item);
      } else {
        _normalized.push([item]);
      }
    });
    return _normalized.map((arr) => arr.sort(sorter)).flat();
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getAsignacionDePlacas({ usuario });
    const idsPlacas = _data.map((item) => item.placa);
    const idsConfigs = _data.map((item) => item.configuracion_de_asignacion_de_placa);
    const _placas = await getManyPlacas(idsPlacas);
    const _configuraciones = await getManyConfiguracionesAsignaciones(idsConfigs);
    setPlacas(_placas);
    setConfiguraciones(_configuraciones);
    setData(normalize(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const permissionPalcas = getModelPermission('placavehicular');
      if (!permissionPalcas.hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        setHasAllViewPermission(permissionPalcas.hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const _data = await getAsignacionDePlacas({ usuario });
      const idsPlacs = _data.map((item) => item.placa);
      const idsConfigs = _data.map((item) => item.configuracion_de_asignacion_de_placa);
      const _placas = await getManyPlacas(idsPlacs);
      const _configuraciones = await getManyConfiguracionesAsignaciones(idsConfigs);
      if (mounted) {
        setPlacas(_placas);
        setConfiguraciones(_configuraciones);
        setData(normalize(_data));
        setLoading(false);
      }
    };
    validatePermission();
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setMutable(true);
    setConfiguracionesTemp([]);
  };

  const onClickEdit = async () => setVisible(true);

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue({
      ...record,
      numero_de_placa: placas.find((e) => e.id === record.placa)?.numero_de_placa,
    });
    const _configuracion = configuraciones
      .find((e) => e.id === record.configuracion_de_asignacion_de_placa);
    setConfiguracionesTemp([_configuracion]);
    setMutable(isMutable(record));
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      values.usuario = usuario;
      values.digitos_de_consecutivo = parseInt(values.digitos_de_consecutivo, 10);
      if (!values.cadena_final) {
        values.cadena_final = '';
      }
      if (key) {
        delete values.numero_de_placa;
      }
      const saved = await (key ? patchAsignacionDePlacas(key, values)
        : postAsignacionDePlacas(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Posición',
      dataIndex: 'orden',
      key: 'orden',
      width: 100,
    },
    {
      titleText: 'Placa',
      dataIndex: 'placa',
      key: 'placa',
      width: 100,
      render: (value) => placas.find((e) => e.id === value)?.numero_de_placa,
    },
    {
      titleText: 'Configuración',
      dataIndex: 'configuracion_de_asignacion_de_placa',
      key: 'configuracion_de_asignacion_de_placa',
      width: 250,
      render: (val) => configuraciones.find((t) => t.id === val)?.descripcion,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => estadosGlobales.find((t) => t.id === val)?.descripcion,
    },
  ];

  const rules = {
    required: [{ required: true }],
  };

  const minRule = () => ({
    validator: async (rule, value) => {
      const { numero_inicial } = form.getFieldsValue();

      if (parseInt(value, 10) < parseInt(numero_inicial, 10)) {
        throw new Error(`Este campo no puede ser menor a ${numero_inicial}`);
      }
    },
  });

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => setVisibleAlert(true);

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteAsignacionDePlacas(key);
    if (deleted) {
      onCancel();
      setVisibleAlert(false);
      await fetchData();
    }
    setLoading(false);
  };

  const updatePositions = async (key, orden) => {
    setLoading(true);
    await patchAsignacionDePlacas(key, { orden });
    await fetchData();
    setLoading(false);
  };

  const moveTo = (where) => {
    setLoading(true);
    if (usuario) {
      const [key] = selectedRowKeys;
      const match = data.find((item) => item.id === key);
      const places = {
        first: 1,
        up: match.orden - 1,
        down: match.orden + 1,
        last: data.length,
      };
      const orden = places[where];
      updatePositions(key, orden);
    }
    setLoading(false);
  };

  const item = data.find((e) => e.id === selectedRowKeys[0]);
  const _data = data.filter((e) => e.configuracion_de_asignacion_de_placa === item
    ?.configuracion_de_asignacion_de_placa);

  const SortControls = () => (
    <Col className="sorting">
      <Button
        type="link"
        icon={<DoubleRightOutlined />}
        disabled={!selectedRowKeys.length || item?.orden <= 2}
        onClick={() => moveTo('first')}
      />
      <Button
        type="link"
        icon={<RightOutlined />}
        disabled={!selectedRowKeys.length || item?.orden < 2}
        onClick={() => moveTo('up')}
      />
      <Button
        type="link"
        icon={<RightOutlined />}
        disabled={!selectedRowKeys.length || item?.orden === _data.length}
        onClick={() => moveTo('down')}
      />
      <Button
        type="link"
        icon={<DoubleRightOutlined />}
        disabled={!selectedRowKeys.length || item?.orden >= _data.length - 1}
        onClick={() => moveTo('last')}
      />
    </Col>
  );

  return (
    <Row id="sort-rows" align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Row>
          <Col style={{ width: `calc(100% - ${isMutable ? 50 : 0}px)` }}>
            <Table
              cols={columns}
              data={data}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              allowImport
              baseURI={baseURI}
              controls={{
                onClickAdd,
                onClickEdit,
                onClickDelete,
              }}
              loading={loading}
              mobileColIndex={0}
              allowSort={false}
              allowSearch={false}
              rowKey="id"
              permission={permission}
              hasAllViewPermission={hasAllViewPermission}
            />
          </Col>
          <SortControls />
        </Row>
        <Modal
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'}`}
              onCancel={onCancel}
              onFinish={onFinish}
              loading={loading}
              permission={permission}
            />
          )}
          visible={visible}
          onCancel={onCancel}
          closable={false}
          footer={null}
        >
          <Col span={24}>
            <Form
              layout="vertical"
              form={form}
              scrollToFirstError
              onFinish={() => onFinish()}
              initialValues={{ estados_globales: 1 }}
            >
              <Row gutter={10}>
                <Col span={24}>
                  <Form.Item
                    name="numero_de_placa"
                    label="Cadena Inicial"
                    tooltip="Valores iniciales (En caso de requerirlas). Ejemplo : REA"
                    hasFeedback
                  >
                    <Input allowClear disabled={!!selectedRowKeys.length} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="numero_inicial"
                    label="Número Inicial"
                    hasFeedback
                    tooltip="Valor incial del rango de la serie de la placa"
                    rules={rules.required}
                  >
                    <Input allowClear disabled={!!selectedRowKeys.length} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="numero_final"
                    label="Número Final"
                    hasFeedback
                    tooltip="Valor final del rango de la serie de la placa"
                    rules={[...rules.required, minRule]}
                  >
                    <Input allowClear disabled={!!selectedRowKeys.length} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <NumericInput
                    name="digitos_de_consecutivo"
                    label="Digitos de Consecutivo"
                    hasFeedback
                    required
                    tooltip="Cantidad de 0's que llevara el consecutivo según sea el caso"
                  />
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="cadena_final"
                    label="Cadena Final"
                    hasFeedback
                    tooltip="Valores finales en caso de requerirlos."
                  >
                    <Input allowClear disabled={!!selectedRowKeys.length} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="orden"
                    label="Orden"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label="Configuración de asignación"
                    name="configuracion_de_asignacion_de_placa"
                    rules={rules.required}
                  >
                    <Select
                      dataSource={configuracionesTemp.length
                        ? configuracionesTemp : configuracionesRef.current}
                      usingExternalDataSource={Boolean(configuracionesTemp.length)}
                      onClear={() => {
                        if (configuracionesTemp.length) {
                          setConfiguracionesTemp([]);
                        }
                      }}
                      onChange={(val) => {
                        const orden = data
                          .filter((e) => e.configuracion_de_asignacion_de_placa === val).length + 1;
                        form.setFieldsValue({ orden });
                      }}
                      pagination={{
                        getter: getConfiguracionesDeAsignacionDePlacas,
                        setter: (dataSource) => {
                          configuracionesRef.current = dataSource;
                        },
                        config: {
                          auto: true,
                          qName: 'descripcion',
                          selfValue: configuracion_de_asignacion_de_placa,
                        },
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="estados_globales"
                    rules={rules.required}
                    label="Estado"
                    hasFeedback
                  >
                    <Select
                      disabled={!selectedRowKeys.length || !mutable}
                      dataSource={estadosGlobales}
                    />
                  </Form.Item>
                </Col>
                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Row>
            </Form>
          </Col>
        </Modal>
      </Spin>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="placa seleccionada"
        loading={loading}
      />
    </Row>
  );
};

Placas.propTypes = {
  permission: PropTypes.objectOf(PropTypes.bool).isRequired,
  usuario: PropTypes.number.isRequired,
};

export default Placas;
